// src/pages1/Savings.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardCanvas from "../components1/DashboardCanvas";
import { fetchUsers } from "../service/APIService";
import useSheetDismiss from "../utils/useSheetDismiss";
import {
  BUCKETS,
  counts,
  createSaving,
  deleteSaving,
  formatAmount,
  getAllAvailableCurrencies,
  getConfiguredCurrencies,
  getSavings,
  getSavingsCurrencyConfig,
  groupByBucket,
  isSavingsEnabled,
  netWorthOverTime,
  saveSavingsCurrencyConfig,
  symbolFor,
  totalsByCurrency,
  totalsByOwner,
  updateSaving,
} from "../service/SavingsService";

/**
 * Savings: what each account is worth now, and what it was worth before.
 *
 * Deliberately apart from the expense pages — it answers a different question,
 * on a different rhythm. Both partners see and edit the same accounts.
 */

const VIEWS = { OVERVIEW: "overview", ACCOUNTS: "accounts", HISTORY: "history" };

const initialsOf = (owner) => (owner || "?").trim().charAt(0).toUpperCase();

const shortDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { day: "numeric", month: "short" });
};

const Savings = () => {
  const navigate = useNavigate();

  const [email] = useState(() => {
    try {
      return String(localStorage.getItem("loggedInUserEmail") || "").toLowerCase();
    } catch {
      return "";
    }
  });

  const [myName] = useState(() => {
    try {
      const me = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
      return String(me?.name || "").trim() || (email ? email.split("@")[0] : "");
    } catch {
      return email ? email.split("@")[0] : "";
    }
  });

  const [members, setMembers] = useState(() => (email ? [email] : []));

  // display names are derived; the owner stored on a row is always an email
  // each member's own name, looked up once; the email prefix is only a stand-in
  // until it arrives
  const [namesByEmail, setNamesByEmail] = useState({});

  const nameOf = useCallback(
    (who) => {
      if (!who) return "";
      if (who === "joint") return "Joint";
      const key = String(who).toLowerCase();
      if (namesByEmail[key]) return namesByEmail[key];
      if (key === email) return myName || email.split("@")[0];
      return String(who).split("@")[0];
    },
    [email, myName, namesByEmail]
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(VIEWS.OVERVIEW);
  const [activeCurrencies, setActiveCurrencies] = useState(() => getConfiguredCurrencies());
  const [currency, setCurrency] = useState(() => getSavingsCurrencyConfig().defaultCode || "INR");
  const [showCurrencySheet, setShowCurrencySheet] = useState(false);
  const [editing, setEditing] = useState(null); // the account in the sheet, or "new"
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const allowed = isSavingsEnabled(email);

  // gate it here too: the route is only as private as the page itself
  useEffect(() => {
    if (!allowed) navigate("/app1/home", { replace: true });
  }, [allowed, navigate]);

  const load = useCallback(async (groupMembers) => {
    setLoading(true);
    try {
      const found = await getSavings(groupMembers);
      if (mounted.current) setRows(found);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed || !email) return;

    (async () => {
      let group = [email];
      try {
        const me = await fetchUsers(email);
        const shared = typeof me?.sharedWith === "string" ? me.sharedWith : "";
        group = Array.from(
          new Set(
            [email, ...shared.split(",").map((e) => e.trim().toLowerCase())].filter(Boolean)
          )
        );
      } catch {
        /* on your own until the lookup works */
      }
      if (!mounted.current) return;
      setMembers(group);
      load(group);

      // ask each member's record what they are called
      const named = await Promise.all(
        group.map(async (who) => {
          try {
            const record = await fetchUsers(who);
            return [who, String(record?.name || "").trim()];
          } catch {
            return [who, ""];
          }
        })
      );
      if (!mounted.current) return;
      setNamesByEmail((prev) => {
        const next = { ...prev };
        named.forEach(([who, name]) => {
          if (name) next[who] = name;
        });
        return next;
      });
    })();
  }, [allowed, email, load]);

  useEffect(() => {
    const handleOpenAdd = () => setEditing("new");
    window.addEventListener("open-add-saving", handleOpenAdd);
    return () => window.removeEventListener("open-add-saving", handleOpenAdd);
  }, []);

  useEffect(() => {
    const syncCurrencies = () => {
      const configured = getConfiguredCurrencies();
      const cfg = getSavingsCurrencyConfig();
      setActiveCurrencies(configured);
      setCurrency((prev) =>
        configured.some((c) => c.code === prev) ? prev : cfg.defaultCode || configured[0]?.code || "INR"
      );
    };
    window.addEventListener("savings-currencies-updated", syncCurrencies);
    return () => window.removeEventListener("savings-currencies-updated", syncCurrencies);
  }, []);

  const [filterOwner, setFilterOwner] = useState(null); // null means all, or email string, or "joint"

  /* ------------------------------- the numbers ------------------------------- */

  const inCurrency = useMemo(() => rows.filter((row) => (row.currency || "INR") === currency), [rows, currency]);
  const totals = useMemo(() => totalsByCurrency(rows), [rows]);
  const byOwner = useMemo(() => totalsByOwner(rows, currency), [rows, currency]);

  const displayRows = useMemo(() => {
    if (!filterOwner) return inCurrency;
    const target = filterOwner.trim().toLowerCase();
    return inCurrency.filter((row) => {
      const owner = (row.owner || "joint").trim().toLowerCase();
      return owner === target;
    });
  }, [inCurrency, filterOwner]);

  const buckets = useMemo(() => groupByBucket(displayRows), [displayRows]);
  const apart = useMemo(() => displayRows.filter((row) => !counts(row)), [displayRows]);
  const apartTotal = useMemo(() => apart.reduce((sum, row) => sum + (Number(row.value) || 0), 0), [apart]);
  const series = useMemo(() => {
    const subset = filterOwner
      ? rows.filter((row) => ((row.owner || "joint").trim().toLowerCase()) === filterOwner.trim().toLowerCase())
      : rows;
    return netWorthOverTime(subset, currency);
  }, [rows, filterOwner, currency]);
  const symbol = symbolFor(currency);

  const lastTouched = useMemo(() => {
    const sorted = displayRows
      .filter((row) => row.updatedAt)
      .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
    return sorted[0] || null;
  }, [displayRows]);

  const bucketMax = Math.max(1, ...buckets.map((entry) => entry.total));

  /* --------------------------------- writing --------------------------------- */

  const { closing, requestClose } = useSheetDismiss(() => setEditing(null));

  const saveSheet = async (form) => {
    setSaving(true);
    setError("");
    try {
      if (editing === "new") {
        await createSaving({ ...form, group: members.join(","), email, by: email });
      } else {
        await updateSaving(editing, { ...form, by: email });
      }
      await load(members);
      requestClose();
    } catch (err) {
      console.error("Could not save the account:", err);
      setError("Could not save that. Try again.");
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const saveHistoryOnly = async (nextHistory) => {
    if (!editing || editing === "new") return;
    setSaving(true);
    setError("");
    try {
      const updated = await updateSaving(editing, {
        history: nextHistory,
        replaceHistory: true,
        by: email,
      });
      await load(members);
      if (mounted.current) setEditing(updated);
    } catch (err) {
      console.error("Could not update recent entry:", err);
      setError("Could not update that entry. Try again.");
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  const removeAccount = async (row) => {
    setSaving(true);
    try {
      await deleteSaving(row.id);
      await load(members);
      requestClose();
    } catch (err) {
      console.error("Could not remove the account:", err);
      setError("Could not remove that.");
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  if (!allowed) return null;

  return (
    <div className="savings-page">
      <DashboardCanvas theme="savings" />
      {/* ------------------------------- the block ------------------------------- */}
      <div className="savings-hero">
        <div className="savings-hero-top">
          <div className="savings-hero-title">
            <span>Savings</span>
            <small>{members.map(nameOf).join(" & ") || "You"}</small>
          </div>
        </div>

        <div className="savings-hero-label">TOGETHER</div>
        <div className="savings-hero-totals">
          {activeCurrencies.map(({ code }) => (
            <div
              key={code}
              className={`savings-hero-amount${code === currency ? " is-main" : ""}`}
              onClick={() => setCurrency(code)}
              style={{ cursor: "pointer" }}
            >
              {symbolFor(code)}
              {formatAmount(totals[code] || 0, code)}
            </div>
          ))}
        </div>
        <div className="savings-hero-hint">kept apart, not converted</div>

        <div className={`savings-people${filterOwner ? " has-filter" : ""}`}>
          {members.map((who, index) => {
            const isSelected = Boolean(filterOwner && filterOwner.toLowerCase() === who.toLowerCase());
            return (
              <button
                key={who}
                type="button"
                className={`savings-person${isSelected ? " is-active" : ""}`}
                onClick={() => setFilterOwner((prev) => (prev && prev.toLowerCase() === who.toLowerCase() ? null : who))}
                aria-pressed={isSelected}
                title={isSelected ? `Filtering by ${nameOf(who)} (click to show all)` : `Filter by ${nameOf(who)}`}
              >
                <div className="savings-person-top">
                  <span className={`savings-chip ${index === 0 ? "is-one" : "is-two"}`}>{initialsOf(nameOf(who))}</span>
                  <span>{nameOf(who)}</span>
                </div>
                <div className="savings-person-amount">
                  {symbol}
                  {formatAmount(byOwner[who] || 0, currency)}
                </div>
              </button>
            );
          })}
          {(members.length > 1 || (byOwner.joint && byOwner.joint > 0)) && (() => {
            const isSelected = filterOwner === "joint";
            return (
              <button
                type="button"
                className={`savings-person${isSelected ? " is-active" : ""}`}
                onClick={() => setFilterOwner((prev) => (prev === "joint" ? null : "joint"))}
                aria-pressed={isSelected}
                title={isSelected ? "Filtering by joint (click to show all)" : "Filter by joint"}
              >
                <div className="savings-person-top">
                  <span className="savings-chip is-joint">J</span>
                  <span>joint</span>
                </div>
                <div className="savings-person-amount">
                  {symbol}
                  {formatAmount(byOwner.joint || 0, currency)}
                </div>
              </button>
            );
          })()}
        </div>
      </div>

      {/* --------------------------------- switches --------------------------------- */}
      <div className="savings-controls">
        <div className="segmented-control savings-views">
          {[
            [VIEWS.OVERVIEW, "Overview"],
            [VIEWS.ACCOUNTS, "Accounts"],
            [VIEWS.HISTORY, "Over time"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`segmented-btn${view === key ? " active" : ""}`}
              onClick={() => setView(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="savings-currency">
          {activeCurrencies.map(({ code, symbol: sym }) => (
            <button
              key={code}
              type="button"
              className={code === currency ? "active" : ""}
              onClick={() => setCurrency(code)}
            >
              {sym} {code}
            </button>
          ))}
          <button
            type="button"
            className="savings-currency-cfg-btn"
            onClick={() => setShowCurrencySheet(true)}
            title="Configure currencies (₹ / $)"
            aria-label="Configure currencies"
          >
            <i className="fi fi-rr-settings-sliders"></i>
          </button>
        </div>
      </div>

      {filterOwner && (
        <div className="savings-filter-banner">
          <span>
            Showing <strong>{filterOwner === "joint" ? "joint" : nameOf(filterOwner)}</strong> ({displayRows.length} {displayRows.length === 1 ? "account" : "accounts"})
          </span>
          <button type="button" onClick={() => setFilterOwner(null)} aria-label="Show all accounts">
            Show all
          </button>
        </div>
      )}

      {loading && <div className="savings-empty">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="savings-empty">
          <p>No accounts yet.</p>
          <button type="button" className="btn-coral-modern" onClick={() => setEditing("new")}>
            Add the first one
          </button>
        </div>
      )}

      {/* --------------------------------- overview --------------------------------- */}
      {!loading && rows.length > 0 && view === VIEWS.OVERVIEW && (
        <div className="savings-body">
          {displayRows.length === 0 ? (
            <div className="savings-card savings-empty">
              <p>No accounts found for {filterOwner === "joint" ? "joint" : nameOf(filterOwner)} in {currency}.</p>
            </div>
          ) : (
            <>
              <div className="savings-card">
                <div className="savings-card-head">Where it sits</div>
                {buckets.map((entry) => (
                  <div key={entry.bucket} className="savings-bar-row">
                    <div className="savings-bar-top">
                      <span>{entry.bucket}</span>
                      <span>
                        {symbol}
                        {formatAmount(entry.total, currency)}
                      </span>
                    </div>
                    <div className="savings-bar-track">
                      <div className="savings-bar-fill" style={{ width: `${Math.max(3, (entry.total / bucketMax) * 100)}%` }}></div>
                    </div>
                    <div className="savings-bar-names">{entry.items.map((row) => row.name).join(" · ")}</div>
                  </div>
                ))}

                {apart.length > 0 && (
                  <div className="savings-apart">
                    <div className="savings-bar-top">
                      <span>Not counted</span>
                      <span>
                        {symbol}
                        {formatAmount(apartTotal, currency)}
                      </span>
                    </div>
                    <div className="savings-bar-names">{apart.map((row) => row.name).join(" · ")} · switch on to count</div>
                  </div>
                )}
              </div>

              {lastTouched && (() => {
                const lastDiff =
                  Array.isArray(lastTouched.history) && lastTouched.history.length >= 2
                    ? (Number(lastTouched.history[0].value) || 0) - (Number(lastTouched.history[1].value) || 0)
                    : null;
                return (
                  <div className="savings-last" onClick={() => setEditing(lastTouched)} style={{ cursor: "pointer" }}>
                    <span className="savings-chip is-one">{initialsOf(nameOf(lastTouched.updatedBy))}</span>
                    <span className="savings-last-text">
                      <strong>{nameOf(lastTouched.updatedBy)}</strong> updated {lastTouched.name}
                      <small>
                        {symbolFor(lastTouched.currency)}
                        {formatAmount(lastTouched.value, lastTouched.currency)}
                        {lastDiff !== null && lastDiff !== 0 && (
                          <span className={`savings-inline-diff ${lastDiff > 0 ? "is-up" : "is-down"}`}>
                            {" "}({lastDiff > 0 ? "+" : "−"}
                            {symbolFor(lastTouched.currency)}
                            {formatAmount(Math.abs(lastDiff), lastTouched.currency)})
                          </span>
                        )}
                        {" · "}
                        {shortDate(lastTouched.updatedAt)}
                      </small>
                    </span>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* --------------------------------- accounts --------------------------------- */}
      {!loading && rows.length > 0 && view === VIEWS.ACCOUNTS && (
        <div className="savings-body">
          {displayRows.length === 0 ? (
            <div className="savings-card savings-empty">
              <p>No accounts found for {filterOwner === "joint" ? "joint" : nameOf(filterOwner)} in {currency}.</p>
            </div>
          ) : (
            buckets.map((entry) => (
              <div key={entry.bucket} className="savings-bucket">
                <div className="savings-bucket-head">
                  <b>{entry.bucket}</b>
                  <span>
                    {symbol}
                    {formatAmount(entry.total, currency)}
                  </span>
                </div>
                <div className="savings-card">
                  {entry.items.map((row) => {
                    const rowDiff =
                      Array.isArray(row.history) && row.history.length >= 2
                        ? (Number(row.value) || 0) - (Number(row.history[1].value) || 0)
                        : null;
                    return (
                      <button key={row.id} type="button" className={`savings-row${counts(row) ? "" : " is-apart"}`} onClick={() => setEditing(row)}>
                        <span className={`savings-chip ${row.owner === email ? "is-one" : row.owner === "joint" ? "is-joint" : "is-two"}`}>
                          {initialsOf(nameOf(row.owner))}
                        </span>
                        <span className="savings-row-name">
                          <b>{row.name}</b>
                          <small>{row.note || (counts(row) ? "" : "not counted")}</small>
                        </span>
                        <span className="savings-row-amount">
                          <b>
                            {symbolFor(row.currency)}
                            {formatAmount(row.value, row.currency)}
                          </b>
                          <small>
                            {rowDiff !== null && rowDiff !== 0 && (
                              <span className={`savings-inline-diff ${rowDiff > 0 ? "is-up" : "is-down"}`}>
                                {rowDiff > 0 ? "+" : "−"}
                                {symbolFor(row.currency)}
                                {formatAmount(Math.abs(rowDiff), row.currency)}
                                {" · "}
                              </span>
                            )}
                            {shortDate(row.updatedAt)}
                          </small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* --------------------------------- over time --------------------------------- */}
      {!loading && rows.length > 0 && view === VIEWS.HISTORY && (
        <div className="savings-body">
          <SavingsChart
            series={series}
            symbol={symbol}
            currency={currency}
            filterLabel={filterOwner ? (filterOwner === "joint" ? "Joint" : nameOf(filterOwner)) : "Together"}
          />
          <div className="savings-note">
            Every line here is drawn from the balances you save — nothing else to keep up to date.
          </div>
        </div>
      )}

      {/* ---------------------------------- sheet ---------------------------------- */}
      {editing && (
        <AccountSheet
          account={editing === "new" ? null : editing}
          members={members}
          nameOf={nameOf}
          currentUserEmail={email}
          closing={closing}
          saving={saving}
          error={error}
          symbol={symbol}
          currencies={activeCurrencies}
          defaultCurrency={currency}
          defaultOwner={filterOwner || ""}
          onClose={requestClose}
          onSave={saveSheet}
          onSaveHistory={saveHistoryOnly}
          onRemove={removeAccount}
        />
      )}

      {showCurrencySheet && <CurrencyConfigSheet onClose={() => setShowCurrencySheet(false)} />}
    </div>
  );
};

/* ------------------------------------ chart ------------------------------------ */

const SavingsChart = ({ series, symbol, currency, filterLabel = "Together", inSheet = false }) => {
  if (series.length < 2) {
    return (
      <div className={`savings-card savings-chart-empty${inSheet ? " is-in-sheet" : ""}`}>
        The graph appears once a balance has been updated at least twice.
      </div>
    );
  }

  const values = series.map((point) => point.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low || 1;
  const last = series[series.length - 1];
  const first = series[0];
  const prev = series[series.length - 2];
  const totalDiff = last.value - first.value;
  const lastDiff = last.value - prev.value;

  const coords = series.map((point, index) => {
    const x = (index / (series.length - 1)) * 300;
    const y = 92 - ((point.value - low) / span) * 80;
    return { x, y, point };
  });

  const points = coords.map((c) => `${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const gradId = inSheet ? "savingsFillSheet" : "savingsFill";

  return (
    <div className={`savings-card savings-chart${inSheet ? " is-in-sheet" : ""}`}>
      <div className="savings-chart-head">
        <div>
          <b>{filterLabel}</b>
          <small>
            {shortDate(first.at)} — {shortDate(last.at)} · {series.length} updates
          </small>
        </div>
        <div className="savings-chart-now">
          <b>
            {symbol}
            {formatAmount(last.value, currency)}
          </b>
          <small className={totalDiff >= 0 ? "is-up" : "is-down"}>
            {totalDiff >= 0 ? "+" : "−"}
            {symbol}
            {formatAmount(Math.abs(totalDiff), currency)} overall
          </small>
          {series.length > 2 && lastDiff !== totalDiff && (
            <small className={`savings-chart-last-diff ${lastDiff >= 0 ? "is-up" : "is-down"}`}>
              Last: {lastDiff >= 0 ? "+" : "−"}
              {symbol}
              {formatAmount(Math.abs(lastDiff), currency)}
            </small>
          )}
        </div>
      </div>

      <svg viewBox="0 0 300 100" className="savings-chart-svg" role="img" aria-label={`Savings from ${shortDate(first.at)} to ${shortDate(last.at)}`}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3D4A7A" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#3D4A7A" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon fill={`url(#${gradId})`} points={`${points} 300,100 0,100`} />
        <polyline fill="none" stroke="#3D4A7A" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" points={points} />
        {coords.map((c, idx) => (
          <circle
            key={idx}
            cx={c.x.toFixed(1)}
            cy={c.y.toFixed(1)}
            r={idx === coords.length - 1 ? "3.8" : "2.6"}
            fill={idx === coords.length - 1 ? "#3D4A7A" : "#FFFFFF"}
            stroke="#3D4A7A"
            strokeWidth="1.8"
          />
        ))}
      </svg>
    </div>
  );
};

/* ------------------------------------ sheet ------------------------------------ */

const toDateInput = (iso) => {
  if (!iso) return new Date().toISOString().slice(0, 10);
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const mergeDateWithIso = (dateStr, originalIso) => {
  if (!dateStr) return originalIso || new Date().toISOString();
  if (originalIso && toDateInput(originalIso) === dateStr) return originalIso;
  const orig = originalIso ? new Date(originalIso) : new Date();
  const parts = dateStr.split("-").map(Number);
  if (parts.length === 3 && !isNaN(orig.getTime())) {
    const merged = new Date(orig);
    merged.setFullYear(parts[0], parts[1] - 1, parts[2]);
    return merged.toISOString();
  }
  return new Date(`${dateStr}T12:00:00`).toISOString();
};

const AccountSheet = ({
  account,
  members,
  nameOf,
  currentUserEmail,
  closing,
  saving,
  error,
  currencies = [],
  defaultCurrency,
  defaultOwner,
  onClose,
  onSave,
  onSaveHistory,
  onRemove,
}) => {
  const [form, setForm] = useState(() => ({
    name: account?.name || "",
    owner: account?.owner || defaultOwner || members[0] || "",
    bucket: account?.bucket || BUCKETS[0],
    currency: account?.currency || defaultCurrency,
    value: account ? String(account.value ?? "") : "",
    note: account?.note || "",
    countInTotal: account ? account.countInTotal !== false : true,
    history: account && Array.isArray(account.history) ? account.history.map((p) => ({ ...p })) : [],
  }));

  // removing takes the word "confirm": one stray tap should not cost an account
  // and everything ever recorded against it
  const [removing, setRemoving] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [editingIdx, setEditingIdx] = useState(null); // index in previous, or "new"
  const [draftEntry, setDraftEntry] = useState({ value: "", date: toDateInput() });

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const ready = form.name.trim() && String(form.value).trim() !== "";
  const previous = useMemo(() => (Array.isArray(form.history) ? form.history : []), [form.history]);
  const accountSeries = useMemo(() => {
    if (!previous.length) return [];
    return [...previous]
      .filter((p) => p && p.at !== undefined)
      .sort((a, b) => String(a.at).localeCompare(String(b.at)))
      .map((p) => ({ at: p.at, value: Number(p.value) || 0, by: p.by }));
  }, [previous]);
  const baseAccountValue = previous.length > 0 ? Number(previous[0].value) || 0 : Number(account?.value) || 0;
  const changed = account && Number(form.value) !== baseAccountValue;
  const sheetSymbol = symbolFor(form.currency);
  const currencyOptions = useMemo(() => {
    const list = currencies.length > 0 ? [...currencies] : getConfiguredCurrencies();
    if (form.currency && !list.some((c) => c.code === form.currency)) {
      list.push({ code: form.currency, symbol: symbolFor(form.currency), label: form.currency });
    }
    return list;
  }, [currencies, form.currency]);

  const startEditRecent = (index, point) => {
    setEditingIdx(index);
    setDraftEntry({
      value: String(point.value ?? ""),
      date: toDateInput(point.at),
    });
  };

  const startAddRecent = () => {
    setEditingIdx("new");
    setDraftEntry({
      value: "",
      date: toDateInput(new Date().toISOString()),
    });
  };

  const applyRecentEdit = async () => {
    if (String(draftEntry.value).trim() === "") return;
    const numVal = Number(draftEntry.value) || 0;
    let nextHistory;

    if (editingIdx === "new") {
      const newPoint = {
        value: numVal,
        at: mergeDateWithIso(draftEntry.date, null),
        by: currentUserEmail || account?.updatedBy || form.owner,
      };
      nextHistory = [newPoint, ...previous];
    } else {
      nextHistory = previous.map((item, idx) =>
        idx === editingIdx
          ? {
              ...item,
              value: numVal,
              at: mergeDateWithIso(draftEntry.date, item.at),
            }
          : item
      );
    }

    nextHistory.sort((a, b) => String(b.at).localeCompare(String(a.at)));
    const latestVal = nextHistory.length > 0 ? String(nextHistory[0].value) : form.value;

    setForm((prev) => ({
      ...prev,
      value: latestVal,
      history: nextHistory,
    }));
    setEditingIdx(null);

    if (onSaveHistory) {
      await onSaveHistory(nextHistory);
    }
  };

  const deleteRecentEntry = async (index) => {
    if (previous.length <= 1) return;
    const nextHistory = previous
      .filter((_, idx) => idx !== index)
      .sort((a, b) => String(b.at).localeCompare(String(a.at)));
    const latestVal = nextHistory.length > 0 ? String(nextHistory[0].value) : form.value;

    setForm((prev) => ({
      ...prev,
      value: latestVal,
      history: nextHistory,
    }));
    setEditingIdx(null);

    if (onSaveHistory) {
      await onSaveHistory(nextHistory);
    }
  };

  const handleMainSave = () => {
    if (editingIdx !== null && String(draftEntry.value).trim() !== "") {
      const numVal = Number(draftEntry.value) || 0;
      let nextHistory =
        editingIdx === "new"
          ? [
              {
                value: numVal,
                at: mergeDateWithIso(draftEntry.date, null),
                by: currentUserEmail || account?.updatedBy || form.owner,
              },
              ...previous,
            ]
          : previous.map((item, idx) =>
              idx === editingIdx
                ? {
                    ...item,
                    value: numVal,
                    at: mergeDateWithIso(draftEntry.date, item.at),
                  }
                : item
            );
      nextHistory.sort((a, b) => String(b.at).localeCompare(String(a.at)));
      const latestVal = editingIdx === 0 && !changed ? String(nextHistory[0].value) : form.value;
      onSave({ ...form, value: latestVal, history: nextHistory });
      return;
    }
    onSave(form);
  };

  return (
    <div className="bottom-sheet-overlay" onClick={onClose}>
      <div className={`bottom-sheet-content savings-sheet${closing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="bottom-sheet-handle" onClick={onClose}></div>
        <button type="button" className="bottom-sheet-close-corner" onClick={onClose} aria-label="Close">
          <i className="fi fi-rr-cross"></i>
        </button>

        <div className="savings-sheet-head">
          <h5>{account ? account.name : "New account"}</h5>
          <small>{account ? `${nameOf(account.owner)} · ${account.bucket}` : "Where some of the savings sit"}</small>
        </div>

        {!account && (
          <div className="savings-field">
            <label htmlFor="savingName">Name</label>
            <input
              id="savingName"
              type="text"
              value={form.name}
              placeholder="SBI PPF"
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
        )}

        <div className={`savings-amount-card${changed ? " is-changed" : ""}`}>
          <label htmlFor="savingValue">{account ? "New balance" : "Balance"}</label>
          <div className="savings-amount-row">
            <span>{sheetSymbol}</span>
            <input
              id="savingValue"
              type="number"
              inputMode="decimal"
              step="0.01"
              value={form.value}
              placeholder="0"
              onChange={(e) => set("value", e.target.value)}
            />
          </div>
          {account && (
            <div className="savings-amount-was">
              was {sheetSymbol}
              {formatAmount(baseAccountValue, form.currency)}
              {changed && (
                <span className={Number(form.value) >= baseAccountValue ? "is-up" : "is-down"}>
                  {Number(form.value) >= baseAccountValue ? "+" : "−"}
                  {sheetSymbol}
                  {formatAmount(Math.abs(Number(form.value) - baseAccountValue), form.currency)}
                </span>
              )}
            </div>
          )}
        </div>

        {account && (
          <div className="savings-sheet-analytics">
            <SavingsChart
              series={accountSeries}
              symbol={sheetSymbol}
              currency={form.currency}
              filterLabel={`${account.name} growth`}
              inSheet
            />

            <div className="savings-previous">
              <div className="savings-previous-head">
                <span>All Recents ({previous.length})</span>
                <div className="savings-previous-head-right">
                  {previous.length >= 2 && (() => {
                    const netDiff = (Number(previous[0].value) || 0) - (Number(previous[previous.length - 1].value) || 0);
                    return (
                      <span className={`savings-previous-net ${netDiff >= 0 ? "is-up" : "is-down"}`}>
                        Total diff: {netDiff >= 0 ? "+" : "−"}
                        {sheetSymbol}
                        {formatAmount(Math.abs(netDiff), form.currency)}
                      </span>
                    );
                  })()}
                  {editingIdx !== "new" && (
                    <button type="button" className="savings-recent-add-btn" onClick={startAddRecent}>
                      + Add
                    </button>
                  )}
                </div>
              </div>

              <div className="savings-previous-list">
                {editingIdx === "new" && (
                  <div className="savings-previous-row is-editing">
                    <div className="savings-recent-edit-inputs">
                      <div className="savings-recent-amount-input">
                        <span>{sheetSymbol}</span>
                        <input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          placeholder="Amount"
                          value={draftEntry.value}
                          onChange={(e) => setDraftEntry((d) => ({ ...d, value: e.target.value }))}
                          autoFocus
                        />
                      </div>
                      <input
                        type="date"
                        className="savings-recent-date-input"
                        value={draftEntry.date}
                        onChange={(e) => setDraftEntry((d) => ({ ...d, date: e.target.value }))}
                      />
                    </div>
                    <div className="savings-recent-edit-actions">
                      <button
                        type="button"
                        className="savings-recent-action-btn is-save"
                        onClick={applyRecentEdit}
                        disabled={saving || String(draftEntry.value).trim() === ""}
                        title="Save entry"
                      >
                        <i className="fi fi-rr-check"></i>
                      </button>
                      <button
                        type="button"
                        className="savings-recent-action-btn is-cancel"
                        onClick={() => setEditingIdx(null)}
                        title="Cancel"
                      >
                        <i className="fi fi-rr-cross-small"></i>
                      </button>
                    </div>
                  </div>
                )}

                {previous.map((point, index) => {
                  const older = previous[index + 1];
                  const diff = older ? (Number(point.value) || 0) - (Number(older.value) || 0) : null;
                  const isRowEditing = editingIdx === index;

                  if (isRowEditing) {
                    return (
                      <div key={`${point.at}-${index}`} className="savings-previous-row is-editing">
                        <div className="savings-recent-edit-inputs">
                          <div className="savings-recent-amount-input">
                            <span>{sheetSymbol}</span>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              value={draftEntry.value}
                              onChange={(e) => setDraftEntry((d) => ({ ...d, value: e.target.value }))}
                              autoFocus
                            />
                          </div>
                          <input
                            type="date"
                            className="savings-recent-date-input"
                            value={draftEntry.date}
                            onChange={(e) => setDraftEntry((d) => ({ ...d, date: e.target.value }))}
                          />
                        </div>
                        <div className="savings-recent-edit-actions">
                          <button
                            type="button"
                            className="savings-recent-action-btn is-save"
                            onClick={applyRecentEdit}
                            disabled={saving || String(draftEntry.value).trim() === ""}
                            title="Save changes"
                          >
                            <i className="fi fi-rr-check"></i>
                          </button>
                          {previous.length > 1 && (
                            <button
                              type="button"
                              className="savings-recent-action-btn is-delete"
                              onClick={() => deleteRecentEntry(index)}
                              disabled={saving}
                              title="Delete entry"
                            >
                              <i className="fi fi-rr-trash"></i>
                            </button>
                          )}
                          <button
                            type="button"
                            className="savings-recent-action-btn is-cancel"
                            onClick={() => setEditingIdx(null)}
                            title="Cancel"
                          >
                            <i className="fi fi-rr-cross-small"></i>
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`${point.at}-${index}`}
                      className="savings-previous-row is-interactive"
                      onClick={() => startEditRecent(index, point)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          startEditRecent(index, point);
                        }
                      }}
                      title="Tap to edit this recent entry"
                    >
                      <span className="savings-chip is-one">{initialsOf(nameOf(point.by))}</span>
                      <span className="savings-previous-meta">
                        <span className="savings-previous-date">{shortDate(point.at)}</span>
                        <small className="savings-previous-by">{nameOf(point.by)}</small>
                      </span>
                      {diff !== null ? (
                        <span className={`savings-diff-pill ${diff > 0 ? "is-up" : diff < 0 ? "is-down" : "is-flat"}`}>
                          {diff > 0 ? "+" : diff < 0 ? "−" : ""}
                          {sheetSymbol}
                          {formatAmount(Math.abs(diff), form.currency)}
                        </span>
                      ) : (
                        <span className="savings-diff-pill is-initial">Initial</span>
                      )}
                      <b>
                        {sheetSymbol}
                        {formatAmount(point.value, form.currency)}
                      </b>
                      <span className="savings-recent-edit-icon" aria-hidden="true">
                        <i className="fi fi-rr-pencil"></i>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="savings-fields">
          <div className="savings-field">
            <label htmlFor="savingOwner">Whose</label>
            <select id="savingOwner" value={form.owner} onChange={(e) => set("owner", e.target.value)}>
              {members.map((who) => (
                <option key={who} value={who}>
                  {nameOf(who)}
                </option>
              ))}
              <option value="joint">joint</option>
            </select>
          </div>

          <div className="savings-field">
            <label htmlFor="savingBucket">Locked for</label>
            <select id="savingBucket" value={form.bucket} onChange={(e) => set("bucket", e.target.value)}>
              {BUCKETS.map((bucket) => (
                <option key={bucket} value={bucket}>
                  {bucket}
                </option>
              ))}
            </select>
          </div>

          <div className="savings-field">
            <label htmlFor="savingCurrency">Currency</label>
            <select id="savingCurrency" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
              {currencyOptions.map(({ code, symbol: sym }) => (
                <option key={code} value={code}>
                  {sym} {code}
                </option>
              ))}
            </select>
          </div>

          <div className="savings-field">
            <label htmlFor="savingNote">Note</label>
            <input
              id="savingNote"
              type="text"
              value={form.note}
              placeholder="pay until 2036"
              onChange={(e) => set("note", e.target.value)}
            />
          </div>
        </div>

        <div className="savings-toggle-row">
          <span>
            <b>Count in the totals</b>
            <small>Off keeps it on the list but out of every total</small>
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={form.countInTotal}
            aria-label="Count in the totals"
            className={`savings-switch${form.countInTotal ? " is-on" : ""}`}
            onClick={() => set("countInTotal", !form.countInTotal)}
          >
            <span></span>
          </button>
        </div>

        {error && <div className="savings-error">{error}</div>}

        <button type="button" className="btn-coral-modern savings-save" disabled={!ready || saving} onClick={handleMainSave}>
          {saving ? "Saving…" : account ? "Save balance" : "Add account"}
        </button>

        {account && !removing && (
          <button type="button" className="savings-remove" onClick={() => setRemoving(true)}>
            Remove this account
          </button>
        )}

        {account && removing && (
          <div className="savings-remove-confirm">
            <label htmlFor="savingConfirm">
              This removes <strong>{account.name}</strong> and every balance recorded against it.
              Type <strong>confirm</strong> to go ahead.
            </label>
            <input
              id="savingConfirm"
              type="text"
              value={confirmText}
              placeholder="confirm"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck="false"
              onChange={(e) => setConfirmText(e.target.value)}
            />
            <div className="savings-remove-actions">
              <button
                type="button"
                className="savings-remove-cancel"
                onClick={() => {
                  setRemoving(false);
                  setConfirmText("");
                }}
              >
                Keep it
              </button>
              <button
                type="button"
                className="savings-remove-go"
                disabled={confirmText.trim().toLowerCase() !== "confirm" || saving}
                onClick={() => onRemove(account)}
              >
                {saving ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------ currency config sheet ------------------------------ */

const CurrencyConfigSheet = ({ onClose }) => {
  const { closing, requestClose } = useSheetDismiss(onClose);
  const [config, setConfig] = useState(() => getSavingsCurrencyConfig());
  const [customCode, setCustomCode] = useState("");
  const [customSymbol, setCustomSymbol] = useState("");
  const allCurrencies = useMemo(() => getAllAvailableCurrencies(), [config]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCurrency = (code) => {
    const isEnabled = config.enabledCodes.includes(code);
    if (isEnabled && config.enabledCodes.length <= 1) return;
    const nextEnabled = isEnabled
      ? config.enabledCodes.filter((c) => c !== code)
      : [...config.enabledCodes, code];
    const nextDefault = nextEnabled.includes(config.defaultCode) ? config.defaultCode : nextEnabled[0];
    const next = saveSavingsCurrencyConfig({
      ...config,
      enabledCodes: nextEnabled,
      defaultCode: nextDefault,
    });
    setConfig(next);
  };

  const setDefaultCurrency = (code) => {
    const nextEnabled = config.enabledCodes.includes(code)
      ? config.enabledCodes
      : [...config.enabledCodes, code];
    const next = saveSavingsCurrencyConfig({
      ...config,
      enabledCodes: nextEnabled,
      defaultCode: code,
    });
    setConfig(next);
  };

  const handleAddCustom = (e) => {
    e.preventDefault();
    const code = customCode.trim().toUpperCase();
    const symbol = customSymbol.trim() || code;
    if (!code || code.length > 5) return;
    const existsInCustom = (config.customCurrencies || []).some((c) => c.code === code);
    const nextCustom = existsInCustom
      ? config.customCurrencies.map((c) => (c.code === code ? { code, symbol, label: code } : c))
      : [...(config.customCurrencies || []), { code, symbol, label: code }];
    const nextEnabled = config.enabledCodes.includes(code)
      ? config.enabledCodes
      : [...config.enabledCodes, code];
    const next = saveSavingsCurrencyConfig({
      ...config,
      enabledCodes: nextEnabled,
      customCurrencies: nextCustom,
    });
    setConfig(next);
    setCustomCode("");
    setCustomSymbol("");
  };

  return (
    <div className="bottom-sheet-overlay" onClick={requestClose}>
      <div className={`bottom-sheet-content savings-sheet savings-currency-sheet${closing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="bottom-sheet-handle" onClick={requestClose}></div>
        <button type="button" className="bottom-sheet-close-corner" onClick={requestClose} aria-label="Close">
          <i className="fi fi-rr-cross"></i>
        </button>

        <div className="savings-sheet-head">
          <h5>Savings Currencies</h5>
          <small>Choose which currencies (₹, $, €, £…) appear in Savings and set your default</small>
        </div>

        <div className="savings-currency-section-label">Active Currencies</div>
        <div className="savings-currency-grid">
          {allCurrencies.map(({ code, symbol: sym, label }) => {
            const enabled = config.enabledCodes.includes(code);
            const isDefault = config.defaultCode === code;
            return (
              <div key={code} className={`savings-currency-chip-card${enabled ? " is-enabled" : ""}`}>
                <button
                  type="button"
                  className="savings-currency-chip-toggle"
                  onClick={() => toggleCurrency(code)}
                  title={enabled ? "Tap to hide currency" : "Tap to enable currency"}
                >
                  <span className="savings-currency-chip-sym">{sym}</span>
                  <span className="savings-currency-chip-meta">
                    <b>{code}</b>
                    <small>{label || code}</small>
                  </span>
                  <span className={`savings-currency-check${enabled ? " is-on" : ""}`}>
                    <i className="fi fi-rr-check"></i>
                  </span>
                </button>
                {enabled && (
                  <button
                    type="button"
                    className={`savings-currency-default-pill${isDefault ? " is-default" : ""}`}
                    onClick={() => setDefaultCurrency(code)}
                  >
                    {isDefault ? "★ Default" : "Set default"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="savings-currency-section-label" style={{ marginTop: "14px" }}>
          Add Custom Currency
        </div>
        <form className="savings-custom-currency-form" onSubmit={handleAddCustom}>
          <input
            type="text"
            placeholder="Code (e.g. CHF)"
            maxLength={5}
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
          />
          <input
            type="text"
            placeholder="Symbol (e.g. Fr)"
            maxLength={5}
            value={customSymbol}
            onChange={(e) => setCustomSymbol(e.target.value)}
          />
          <button type="submit" disabled={!customCode.trim()}>
            + Add
          </button>
        </form>

        <button type="button" className="btn-coral-modern savings-save" onClick={requestClose}>
          Done
        </button>
      </div>
    </div>
  );
};

export default Savings;

